import {Injectable, NgZone} from "@angular/core";
import {
  default as OneSignalPlugin,
  InAppMessageClickEvent,
  InAppMessageDidDismissEvent,
  InAppMessageDidDisplayEvent,
  InAppMessageWillDismissEvent,
  InAppMessageWillDisplayEvent,
} from "onesignal-cordova-plugin";
import {InAppMessageEventTypeMap} from "onesignal-cordova-plugin/dist/models/InAppMessage";
import { Observable } from "rxjs";


declare type InAppMessageEventName = keyof InAppMessageEventTypeMap;

@Injectable()
export class InAppMessages {
  constructor(private readonly ngZone: NgZone) {
  }

  onClick(): Observable<InAppMessageClickEvent> {
    return this.addListener('click')
  }

  onWillDisplay(): Observable<InAppMessageWillDisplayEvent> {
    return this.addListener('willDisplay')
  }

  onDidDisplay(): Observable<InAppMessageDidDisplayEvent> {
    return this.addListener('didDisplay')
  }

  onWillDismiss(): Observable<InAppMessageWillDismissEvent> {
    return this.addListener('willDismiss')
  }

  onDidDismiss(): Observable<InAppMessageDidDismissEvent> {
    return this.addListener('didDismiss')
  }

  /**
   * Add a trigger for the current user. Triggers are currently explicitly used to determine whether a specific IAM should be displayed to the user.
   */
  addTrigger(key: string, value: string): void {
    OneSignalPlugin.InAppMessages.addTrigger(key, value);
  }

  /**
   * Add multiple triggers for the current user. Triggers are currently explicitly used to determine whether a specific IAM should be displayed to the user.
   */
  addTriggers(triggers: Record<string, string>): void {
    OneSignalPlugin.InAppMessages.addTriggers(triggers);
  }

  /**
   * Remove the trigger with the provided key from the current user.
   */
  removeTrigger(key: string): void {
    OneSignalPlugin.InAppMessages.removeTrigger(key);
  }

  /**
   * Remove multiple triggers from the current user.
   */
  removeTriggers(keys: string[]): void {
    OneSignalPlugin.InAppMessages.removeTriggers(keys);
  }

  /**
   * Clear all triggers from the current user.
   */
  clearTriggers(): void {
    OneSignalPlugin.InAppMessages.clearTriggers();
  }

  /**
   * Set whether in-app messaging is currently paused.
   * When set to true no IAM will be presented to the user regardless of whether they qualify for them.
   * When set to 'false` any IAMs the user qualifies for will be presented to the user at the appropriate time.
   */
  setPaused(pause: boolean): void {
    OneSignalPlugin.InAppMessages.setPaused(pause);
  }

  /**
   * Whether in-app messaging is currently paused.
   */
  getPaused(): Promise<boolean> {
    return this.ngZone.runOutsideAngular(() => new Promise<boolean>((resolve, reject) => {
      OneSignalPlugin.InAppMessages.getPaused()
        .then(
          (result) => {
            this.ngZone.run(() => {
              resolve(result);
            });
          },
          (reason) => {
            this.ngZone.run(() => {
              reject(reason);
            });
          },
        );
    }));
  }

  private addListener<K extends InAppMessageEventName>(event: K): Observable<InAppMessageEventTypeMap[K]> {
    return this.ngZone.runOutsideAngular(() => new Observable<InAppMessageEventTypeMap[K]>((subscriber) => {
      const listener = (event: InAppMessageEventTypeMap[K]) => {
        this.ngZone.run(() => {
          subscriber.next(event);
        });
      }

      OneSignalPlugin.InAppMessages.addEventListener(event, listener);

      return () => {
        OneSignalPlugin.InAppMessages.removeEventListener(event, listener);
      };
    }));
  }
}
